import { useEffect, useRef, useState } from 'react';
import { Box, Button, Collapse, Fade, Slider, Typography, useMediaQuery } from '@mui/material';
import {
    IconAdjustmentsHorizontal,
    IconBrandTelegram,
    IconDownload,
    IconGift,
    IconUsersGroup,
} from '@tabler/icons-react';
import LinkIcon from 'src/icons/Link';
import SubscriptionsService from 'src/services/SubscriptionsService';
import formatNumber from 'src/utils/formatNumber';
import { AUTO_TOPUP_DISCOUNT_PERCENT } from 'src/services/LeadStreamsService/packagePrices';

import type { ReactNode } from 'react';
import type { LeadPackagePrice } from 'src/services/LeadStreamsService/agent';

const PRESET_SIZES = [50, 100, 200];
const PRESET_NAMES: Record<number, string> = { 50: 'Старт', 100: 'Про', 200: 'Бизнес' };

const money = (value: number) => {
    const rounded = Math.round((value + Number.EPSILON) * 100) / 100;
    return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
        maximumFractionDigits: 2,
        style: 'currency',
        currency: 'RUB',
    }).format(rounded);
};

type PriceRow = { label: string; value: string; success?: boolean };

const priceSummary = (price: LeadPackagePrice, baseUnit: number) => {
    const total = Math.round(Number(price.price_per_lead) * price.package_size * 100) / 100;
    const regularTotal = Math.round(baseUnit * price.package_size * 100) / 100;
    return { total, regularTotal, saving: Math.round(Math.max(0, regularTotal - total) * 100) / 100 };
};

const priceRows = (price: LeadPackagePrice, baseUnit: number, monthly: boolean): PriceRow[] => {
    const unit = Number(price.price_per_lead);
    const { saving } = priceSummary(price, baseUnit);

    return [
        { label: 'Цена за лид', value: money(unit) },
        { label: 'Скидка за объём', value: String(Number(price.discount_percent || 0)) + '%', success: Number(price.discount_percent || 0) > 0 },
        ...(monthly ? [{ label: 'Автопополнение', value: `ещё ${AUTO_TOPUP_DISCOUNT_PERCENT}%`, success: true }] : []),
        ...(saving > 0
            ? [
                  { label: 'Выгода', value: money(saving), success: true },
              ]
            : []),
    ];
};

const CardPrice = ({ price, baseUnit, monthly }: { price: LeadPackagePrice; baseUnit: number; monthly: boolean }) => {
    const { total, regularTotal, saving } = priceSummary(price, baseUnit);
    return (
        <Box sx={{ minHeight: 82, mt: 1.5 }}>
            <Typography sx={{ color: 'var(--package-muted, var(--lm-muted))', fontSize: 12 }}>Стоимость</Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.75 }}>
                <Typography sx={{ color: 'var(--package-text, var(--lm-text))', fontSize: { xs: 27, lg: 26, xl: 28 }, fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                    {money(total)}
                </Typography>
                {monthly && <Typography sx={{ color: 'var(--package-muted, var(--lm-muted))', fontSize: 13 }}>/мес</Typography>}
            </Box>
            {saving > 0 && (
                <Typography sx={{ color: 'var(--package-muted, var(--lm-muted))', fontSize: 14, textDecoration: 'line-through' }}>
                    {money(regularTotal)}
                </Typography>
            )}
        </Box>
    );
};

const PriceDetails = ({ rows }: { rows: PriceRow[] }) => (
    <Box sx={{ width: '100%', mt: 1.25 }}>
        {rows.map((row) => (
            <Box
                key={row.label}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 1,
                    py: 0.65,
                    borderTop: '1px solid var(--package-line, var(--lm-border))',
                }}
            >
                <Typography sx={{ color: 'var(--package-muted, var(--lm-muted))', fontSize: 12, textAlign: 'left' }}>
                    {row.label}
                </Typography>
                <Typography
                    className={row.label === 'Цена за лид' ? 'package-unit-price' : undefined}
                    sx={{
                        color: row.success ? 'var(--package-success, var(--lm-success-ink))' : 'var(--package-text, var(--lm-text))',
                        fontSize: 14,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {row.value}
                </Typography>
            </Box>
        ))}
    </Box>
);

const PackageFeatures = ({ size, basePartnerPercent, inline = false }: { size: number; basePartnerPercent: number; inline?: boolean }) => {
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const partnerRate = `${Math.min(100, basePartnerPercent + (size >= 200 ? 10 : 5))}%`;
    const features = [
        ...(size > 50 ? [{ label: `Партнерское вознаграждение ${partnerRate}`, icon: <IconUsersGroup size={17} stroke={1.8} />, emphasis: partnerRate }] : []),
        ...(size >= 200
            ? [{ label: 'Кэшбэк 10% бонусными баллами', icon: <IconGift size={17} stroke={1.8} />, emphasis: '10%' }]
            : size >= 100
            ? [{ label: 'Кэшбэк 5% бонусными баллами', icon: <IconGift size={17} stroke={1.8} />, emphasis: '5%' }]
            : []),
    ];
    const lastVisibleFeatures = useRef(features);
    if (features.length) lastVisibleFeatures.current = features;
    const renderedFeatures = inline && !features.length ? lastVisibleFeatures.current : features;
    const list = (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 1.75, mb: 0, width: '100%', display: inline ? 'flex' : 'block', flexWrap: 'wrap', columnGap: 2 }}>
            {renderedFeatures.map(({ label, icon, emphasis }) => (
                <Box component="li" key={label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.55 }}>
                    <Box component="span" aria-hidden="true" sx={{ color: 'var(--package-icon, var(--lm-muted))', display: 'inline-flex', flexShrink: 0, mt: '1px', '& svg': { width: 17, height: 17 } }}>
                        {icon}
                    </Box>
                    <Typography sx={{ color: 'var(--package-text, var(--lm-text))', fontSize: 13, lineHeight: 1.45, textAlign: 'left' }}>
                        {emphasis ? <>
                            {label.slice(0, label.indexOf(emphasis))}
                            <Box component="span" sx={{ color: 'var(--package-success, var(--lm-success-ink))', fontWeight: 800 }}>{emphasis}</Box>
                            {label.slice(label.indexOf(emphasis) + emphasis.length)}
                        </> : label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
    return inline ? (
        <Collapse in={features.length > 0} timeout={reduceMotion ? 0 : 220} unmountOnExit>
            <Fade in={features.length > 0} appear={false} timeout={reduceMotion ? 0 : 180}>
                <Box aria-live="polite">{list}</Box>
            </Fade>
        </Collapse>
    ) : features.length ? list : null;
};

export const SharedFeatures = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mt: 1.5, pb: 2, borderBottom: '1px solid var(--lm-border)', color: 'var(--lm-muted)' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--lm-text)' }}>Во всех тарифах</Typography>
        {[
            { label: 'Интеграция с CRM', icon: <LinkIcon /> },
            { label: 'Выгрузка лидов', icon: <IconDownload size={17} stroke={1.8} /> },
            { label: 'Моментальные уведомления', icon: <IconBrandTelegram size={17} stroke={1.8} /> },
        ].map(({ label, icon }) => (
            <Box key={label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.65 }}>
                <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: 'var(--lm-accent)', '& svg': { width: 17, height: 17 } }}>{icon}</Box>
                <Typography sx={{ fontSize: 13 }}>{label}</Typography>
            </Box>
        ))}
    </Box>
);

export default function StreamPackagePicker({
    prices,
    selectedSize,
    currentSize = null,
    onSelect,
    disabled = false,
    monthly = false,
    showSharedFeatures = false,
    customOnly = false,
    compactBenefits = false,
    children,
}: {
    prices: LeadPackagePrice[];
    selectedSize: number | null;
    currentSize?: number | null;
    onSelect: (size: number) => void;
    disabled?: boolean;
    monthly?: boolean;
    showSharedFeatures?: boolean;
    customOnly?: boolean;
    compactBenefits?: boolean;
    children?: ReactNode;
}) {
    const [customSelected, setCustomSelected] = useState(selectedSize !== null && !PRESET_SIZES.includes(selectedSize));
    const [customSize, setCustomSize] = useState(selectedSize ?? 30);
    const [basePartnerPercent, setBasePartnerPercent] = useState(10);

    useEffect(() => {
        let mounted = true;
        SubscriptionsService.getMyPromocode()
            .then((promo) => {
                const base = promo.base_reward_percent;
                if (mounted && Number.isInteger(base) && base !== undefined && base >= 0 && base <= 100) {
                    setBasePartnerPercent(base);
                }
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (selectedSize !== null && (customOnly || !PRESET_SIZES.includes(selectedSize))) {
            setCustomSize(selectedSize);
            setCustomSelected(true);
        }
    }, [selectedSize, customOnly]);

    if (!prices.length) return null;

    const availableSizes = prices.map((price) => price.package_size).sort((a, b) => a - b);
    const fullRangeAvailable = Array.from({ length: 98 }, (_, index) => 30 + index * 10).every((size) =>
        availableSizes.includes(size)
    );
    const presets = PRESET_SIZES.map((size) => prices.find((price) => price.package_size === size)).filter(
        (price): price is LeadPackagePrice => Boolean(price)
    );
    const currentCustom = prices.find((price) => price.package_size === customSize) || prices[0];
    const base = prices.reduce((lowest, price) => (price.package_size < lowest.package_size ? price : lowest));
    const baseDiscount = Number(base.discount_percent || 0);
    const baseUnit = Number(base.one_time_price_per_lead || base.price_per_lead) / (1 - baseDiscount / 100);

    const selectCustom = (size: number) => {
        setCustomSize(size);
        setCustomSelected(true);
        onSelect(size);
    };

    const quantitySlider = (
                        <Slider
                            aria-label="Произвольное количество лидов"
                            value={currentCustom.package_size}
                            min={availableSizes[0]}
                            max={availableSizes[availableSizes.length - 1]}
                            step={fullRangeAvailable ? 10 : null}
                            marks={fullRangeAvailable ? false : availableSizes.map((value) => ({ value }))}
                            disabled={disabled || availableSizes.length < 2}
                            onChange={(_event, size) => {
                                if (typeof size === 'number' && availableSizes.includes(size)) selectCustom(size);
                            }}
                            sx={{ color: 'var(--lm-accent-ink)', mt: 1.5, mb: 0.5 }}
                        />
    );

    const quantityControl = (
        <Box sx={{ minWidth: 0, mt: 1.5 }}>
                        <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13 }}>
                            Количество лидов{fullRangeAvailable ? ' · шаг 10' : ''}
                        </Typography>
                        <Typography sx={{ color: 'var(--lm-text)', fontSize: 26, fontWeight: 800, mt: 0.25 }}>
                            {formatNumber(currentCustom.package_size)}
                        </Typography>
                        {quantitySlider}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'var(--lm-muted)', fontSize: 12 }}>
                            <span>{formatNumber(availableSizes[0])}</span>
                            <span>{formatNumber(availableSizes[availableSizes.length - 1])}</span>
                        </Box>
        </Box>
    );

    return (
        <Box>
            <Box
                sx={{
                    display: customOnly ? 'none' : 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                    gap: { xs: 1.5, lg: 2 },
                    alignItems: 'stretch',
                }}
            >
                {presets.map((price) => {
                    const active = !customSelected && price.package_size === selectedSize;
                    const current = currentSize === price.package_size;
                    const isBusiness = price.package_size === 200;
                    const isPro = price.package_size === 100;
                    const cardBackground = isBusiness ? '#20242C' : isPro ? '#EAF1FF' : '#FFF9F1';
                    const cardBorder = isBusiness ? '#414A58' : isPro ? '#A9C4FF' : '#E9DFD2';
                    const cardText = isBusiness ? '#F8FAFD' : '#172338';
                    const selectedBorder = 'var(--lm-accent)';
                    return (
                        <Button
                            key={price.package_size}
                            type="button"
                            disableRipple
                            aria-label={PRESET_NAMES[price.package_size] + ' — ' + formatNumber(price.package_size) + ' лидов'}
                            aria-pressed={active}
                            disabled={disabled}
                            onClick={() => {
                                setCustomSelected(false);
                                onSelect(price.package_size);
                            }}
                            sx={{
                                minWidth: 0,
                                height: 'auto',
                                px: { xs: 2, lg: 2.25 },
                                py: 2,
                                border: '2px solid',
                                borderColor: active ? selectedBorder : cardBorder,
                                borderRadius: '8px',
                                bgcolor: cardBackground,
                                color: cardText,
                                '--package-text': cardText,
                                '--package-muted': isBusiness ? '#B5C0CF' : '#61718B',
                                '--package-line': isBusiness ? '#444D5A' : '#DBE4EF',
                                '--package-success': isBusiness ? '#8FDFB2' : '#168452',
                                '--package-icon': isBusiness ? '#A9C3FF' : isPro ? 'var(--lm-accent-ink)' : '#876643',
                                textTransform: 'none',
                                alignItems: 'stretch',
                                whiteSpace: 'normal',
                                boxShadow: active && isBusiness ? '0 0 0 2px var(--lm-accent)' : 'none',
                                transition: 'none',
                                transform: 'none',
                                animation: 'none',
                                '&:hover': {
                                    bgcolor: cardBackground,
                                    color: cardText,
                                    borderColor: selectedBorder,
                                    boxShadow: isBusiness
                                        ? '0 0 0 3px var(--lm-accent), 0 8px 24px rgba(0, 68, 255, 0.18)'
                                        : '0 0 0 3px rgba(0, 68, 255, 0.16), 0 8px 22px rgba(0, 68, 255, 0.12)',
                                    transform: 'none',
                                },
                                '&:hover .package-lead-count': {
                                    color: isBusiness ? '#fff' : 'var(--lm-accent)',
                                    bgcolor: isBusiness ? 'var(--lm-accent)' : 'transparent',
                                    fontWeight: 900,
                                },
                                '&:hover .package-unit-price': { color: isBusiness ? '#fff' : 'var(--lm-accent)', fontWeight: 900 },
                                '&:focus-visible': { outline: '2px solid var(--lm-accent-ink)', outlineOffset: 2 },
                            }}
                        >
                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, minHeight: 112 }}>
                                    <Box>
                                        <Typography component="h3" variant="h5" sx={{ display: 'block', color: 'var(--package-text)', fontFamily: '"Inter", system-ui', fontSize: { xs: 26, sm: 28 }, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.2 }}>{PRESET_NAMES[price.package_size]}</Typography>
                                        <Typography className="package-lead-count" sx={{ display: 'inline-block', color: active ? (isBusiness ? '#fff' : 'var(--lm-accent-ink)') : 'var(--package-muted)', bgcolor: active && isBusiness ? 'var(--lm-accent)' : 'transparent', borderRadius: '4px', px: isBusiness ? 0.5 : 0, fontSize: 15, fontWeight: active ? 800 : 500, mt: 0.5 }}>
                                            {formatNumber(price.package_size)} лидов
                                        </Typography>
                                    </Box>
                                    {(active || current) && <Typography sx={{ color: active && isBusiness ? '#fff' : isBusiness ? '#A9C3FF' : 'var(--lm-accent-ink)', bgcolor: active && isBusiness ? 'var(--lm-accent)' : 'transparent', px: active && isBusiness ? 0.7 : 0, py: active && isBusiness ? 0.25 : 0, borderRadius: '5px', fontSize: 12, fontWeight: 700, textAlign: 'right' }}>{active ? 'Выбрано' : 'Сейчас'}</Typography>}
                                </Box>
                                <CardPrice price={price} baseUnit={baseUnit} monthly={monthly} />
                                <PriceDetails rows={priceRows(price, baseUnit, monthly)} />
                                {!compactBenefits && <PackageFeatures size={price.package_size} basePartnerPercent={basePartnerPercent} />}
                            </Box>
                        </Button>
                    );
                })}

                {!customOnly && <Box
                    role="region"
                    aria-label="Выбор своего количества лидов"
                    tabIndex={disabled ? -1 : 0}
                    onClick={(event) => {
                        if (!disabled && !(event.target as Element).closest('button,input,.MuiSlider-root')) selectCustom(currentCustom.package_size);
                    }}
                    onKeyDown={(event) => {
                        if (!disabled && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            selectCustom(currentCustom.package_size);
                        }
                    }}
                    sx={{
                        minWidth: 0, p: 2, display: 'flex', flexDirection: 'column', cursor: disabled ? 'default' : 'pointer',
                        '&:focus-visible': { outline: '2px solid var(--lm-accent-ink)', outlineOffset: 2 },
                        border: '2px solid', borderColor: customSelected ? 'var(--lm-accent)' : 'var(--lm-border)',
                        borderRadius: '8px', bgcolor: 'var(--lm-surface)', color: 'var(--lm-text)',
                    }}
                >
                    <Box sx={{ minHeight: 112 }}>
                    <Button
                        type="button"
                        disableRipple
                        aria-label="Своё количество лидов"
                        aria-pressed={customSelected}
                        disabled={disabled}
                        onClick={() => selectCustom(currentCustom.package_size)}
                        sx={{
                            minWidth: 0, minHeight: 24, height: 'auto', p: 0, width: '100%', textAlign: 'left', textTransform: 'none',
                            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1,
                            color: 'var(--lm-text)',
                            '&:hover': { bgcolor: 'transparent', color: 'var(--lm-accent)' },
                            '&:focus-visible': { outline: '2px solid var(--lm-accent-ink)', outlineOffset: 4 },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Typography component="p" sx={{ fontSize: 15, fontWeight: 650, lineHeight: 1.3 }}>Своё количество</Typography>
                        </Box>
                        <IconAdjustmentsHorizontal size={21} stroke={1.8} style={{ flexShrink: 0 }} />
                    </Button>
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontSize: 26, fontWeight: 750, minWidth: 44 }}>{formatNumber(currentCustom.package_size)}</Typography>
                                <Box sx={{ flex: 1, minWidth: 0, '& .MuiSlider-root': { mt: 0, mb: 0 } }}>{quantitySlider}</Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--lm-muted)' }}>
                                <span>{formatNumber(availableSizes[0])}</span><span>{formatNumber(availableSizes[availableSizes.length - 1])}</span>
                            </Box>
                        </Box>
                    </Box>
                    <CardPrice price={currentCustom} baseUnit={baseUnit} monthly={monthly} />
                    <PriceDetails rows={priceRows(currentCustom, baseUnit, monthly)} />
                    {!compactBenefits && <PackageFeatures size={currentCustom.package_size} basePartnerPercent={basePartnerPercent} />}
                </Box>}

            </Box>

            {customOnly && (
                <Box
                    id="stream-custom-package"
                    role="region"
                    aria-label="Выбор своего количества лидов"
                    sx={{
                        mt: 1.5, p: { xs: 2, sm: 2.5 },
                        display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(260px, 0.8fr)' },
                        gap: { xs: 2, md: 4 },
                        border: '1px solid var(--lm-border)', borderRadius: '8px', bgcolor: 'var(--lm-surface)',
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        {quantityControl}
                        {!compactBenefits && <PackageFeatures size={currentCustom.package_size} basePartnerPercent={basePartnerPercent} />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <CardPrice price={currentCustom} baseUnit={baseUnit} monthly={monthly} />
                        <PriceDetails rows={priceRows(currentCustom, baseUnit, monthly)} />
                    </Box>
                </Box>
            )}

            {compactBenefits && selectedSize !== null && <PackageFeatures size={selectedSize} basePartnerPercent={basePartnerPercent} inline />}
            {showSharedFeatures && <SharedFeatures />}
            {children && <Box sx={{ mt: 2 }}>{children}</Box>}
        </Box>
    );
}
